import { Result, Option } from "@nozzlegear/railway";
import { State, CornerState, FrameState, PackageState } from "../types";
import { Page } from "../enums";
import { cornerStateSchema, frameStateSchema, packageStateSchema, purchaseOrderIdSchema } from "./zod";

type ValidateFn<T = any> = (state: State | Partial<State>) => Result<T>;

const validateCorners: (state: State | Partial<State>) => Result<CornerState> = state => {
    const result = cornerStateSchema.safeParse(state.corners);
    if (!result.success)
        return Result.ofError(result.error.message);
    return Result.ofValue(result.data);
};

const validateFrames: (state: State | Partial<State>) => Result<FrameState> = state => {
    const result = frameStateSchema.safeParse(state.frames);
    if (!result.success)
        return Result.ofError(result.error.message);
    return Result.ofValue(result.data);
}

const validatePackages: (state: State | Partial<State>) => Result<PackageState> = state => {
    const result = packageStateSchema.safeParse(state.packages);
    if (!result.success)
        return Result.ofError(result.error.message);
    return Result.ofValue<PackageState>(result.data);
}

const validatePurchaseOrderId: (state: State | Partial<State>) => Result<string> = state => {
    const result = purchaseOrderIdSchema.safeParse(state.purchaseOrderId);
    if (!result.success) {
        return Result.ofError("Purchase Order ID: " + result.error.errors.map(x => x.message).join(", "));
    }
    return Result.ofValue<string>(result.data);
}

export const pagesValidationMap = new Map<Page, ValidateFn[]>([
    [Page.Packages, []],
    [Page.Frames, [validatePurchaseOrderId, validatePackages]],
    [Page.Corners, [validateFrames]],
    [Page.Confirm, [validateCorners]],
]);

/**
 * Applies an array of validators to the state and reduces them to a single Result<unknown>.
 * The intent is for you to check the error value and ignore the ok value.
 */
export function reduceValidators(
    validators: ValidateFn<unknown>[],
    state: State | Partial<State>
): Result<unknown> {
    const initialValue = Result.ofValue(state);

    if (!validators || validators.length === 0)
        return initialValue;

    // Apply each validator
    return validators.reduce<Result<unknown>>((prevValue, currentValue) => {
        if (prevValue.isOk())
            return currentValue(state);
        return prevValue;
    }, initialValue);
}

export function getStateValidationError(page: Page, state: State | Partial<State>): Option<string> {
    // Note: the validation here is offset, i.e. each page validates the state for its previous page
    for (let i: Page = page ?? 0; i <= 0; i--) {
        const validators = pagesValidationMap.get(i) ?? [];
        const validationResult = reduceValidators(validators, state);

        if (validationResult.isError())
            return Option.ofSome(validationResult.getError() as string);
    }

    return Option.ofNone();
}

